import {Injectable} from '@angular/core';
import {DatePipe} from '@angular/common';
import {SafeHtml} from '@angular/platform-browser';
import { HttpClient, HttpResponse } from '@angular/common/http';
import {Observable} from 'rxjs';
import {finalize, take} from 'rxjs/operators';
import {saveAs} from 'file-saver';
import {DateUtil} from '../../common/util/DateUtil';
import {SafePipe} from '../../common/pipes/safe.pipe';
import {SafeStringType} from '../../clm/types/safe-string-type.enum';
import {SharedService} from '../../common/services/shared-service';
import {environment} from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ReportingService {

  private url = environment.baseReportingUrl + '/rest_v2';
  private credentials = environment.reportingCredentials;

  constructor(
    public datePipe: DatePipe,
    private safePipe: SafePipe,
    private httpClient: HttpClient,
    private sharedService: SharedService,
  ) {
  }

  login(): Observable<HttpResponse<object>> {
    return this.httpClient.get<HttpResponse<object>>(`${this.url}/login?${this.credentials}`, {observe: 'response'});
  }

  getListOfReports(): Observable<any> {
    return this.httpClient.get<any>(`${this.url}/resources?type=reportUnit&limit=300&${this.credentials}`);
  }

  getReportHtml(uri: string, parameters: string): Observable<string> {
    return this.httpClient.get(this.buildReportGenerationUrl(uri, 'html', parameters), {responseType: 'text'});
  }

  // TODO: update response type to return PDF
  generateReport(uri: string, fileType: string, parameters: string): Observable<any> {
    return this.httpClient.get(this.buildReportGenerationUrl(uri, fileType, parameters), {responseType: 'blob'});
  }

  getReportParameters(uri: string): Observable<any> {
    return this.httpClient.get(`${this.url}/reports${uri}/inputControls?${this.credentials}`);
  }

  buildReportGenerationUrl(uri: string, fileType: string, parameters: string): string {
    const credentialString = parameters === '' ? this.credentials : `&${this.credentials}`;
    return `${this.url}/reports${uri}.${fileType}?${parameters}${credentialString}`;
  }

  buildEmbeddedReportUrl(uri: string): string {
    const uriString = uri.replace('/', '%2F');
    const flowId = 'viewReportFlow';
    const standalone = 'true';
    const decorate = 'no';
    const viewAsDashboardFrame = 'false';
    return `${environment.baseReportingUrl}/flow.html?_flowId=${flowId}&standAlone=${standalone}&decorate=${decorate}&viewAsDashboardFrame=${viewAsDashboardFrame}&reportUnit=${uriString}&${environment.reportingCredentials}`;
  }

  generateClaimOrAuthorizationReport(isClaim: boolean, transactionId: number): Observable<any> {
    return new Observable(observer => {
      const reportType: string = isClaim ? 'Claim_Reconciliation' : 'Medical_Authorization';
      const reportName: string = isClaim ? 'claim_reconciliation' : 'medical_authorisation';

      this.generateReport(`/Reports/${reportType}`, 'pdf', `Transaction_Id=${transactionId}`)
        .pipe(
          take(1),
          finalize(() => {
            observer.complete();
          })
        )
        .subscribe(
          (response) => {
            saveAs(response, `${reportName}_${transactionId}_${this.datePipe.transform(new Date(), 'yyyyMMdd_HHmm')}.pdf`);
            observer.next({success: true});
          },
          (error) => {
            observer.next({success: false, error});
          });
    });
  }

  generateEmailReport(triggerId: number): Observable<any> {
    return new Observable(observer => {
      const reportType = 'Medical_Authorization';
      const reportName = 'medical_authorisation';

      this.generateReport(`/Reports/${reportType}`, 'pdf', `Transaction_Id=${triggerId}`)
        .pipe(
          take(1),
          finalize(() => {
            observer.complete();
          })
        )
        .subscribe(
          (response) => {
            saveAs(response, `${reportName}_${triggerId}_${this.datePipe.transform(new Date(), 'yyyyMMdd_HHmm')}.pdf`);
            observer.next({success: true});
          },
          (error) => {
            observer.next({success: false, error});
          });
    });
  }

  generateHTMLReport(uri: string, parameters: string): Observable<SafeHtml> {
    return new Observable(observer => {
      this.getReportHtml(`/Reports/${uri}`, parameters)
        .pipe(
          take(1),
          finalize(() => {
            observer.complete();
          })
        )
        .subscribe({
          next: (response: string) => {
            const html: SafeHtml = this.safePipe.transform(response, SafeStringType.Html);
            observer.next(html);
          },
          error: (error) => {
            observer.error(error);
          }
        });
    });
  }

  downloadPDFReport(uri: string, parameters: string, fileNameToSaveAs?: string): void {
    let fileName = fileNameToSaveAs ?? 'default';
    fileName += `${fileName}_${this.datePipe.transform(new Date(), DateUtil.dateTimeFormatForReport)}.pdf`;

    this.generateReport(`/Reports/${uri}`, 'pdf', parameters)
      .pipe(
        take(1)
      )
      .subscribe({
        next: (response) => {
          saveAs(response, fileName);
        },
        error: (_) => {
          this.sharedService.openSnackBar('There was an error generating the report', 'Dismiss');
        }
      });
  }

  // InCase PDF report to be downloaded Directly Instead of GenericReportDialogComponent
  generateApplicationMessagesReport(applicationId: number): Observable<any> {
    return new Observable(observer => {
      const reportType = 'Application_Messages';
      const reportName = 'application_messages';

      this.generateReport(`/Reports/${reportType}`, 'pdf', `Application_ID=${applicationId}`)
        .pipe(
          take(1)
        )
        .subscribe({
          next: (response) => {
            saveAs(response, `${reportName}_${applicationId}_${this.datePipe.transform(new Date(), 'yyyyMMdd_HHmm')}.pdf`);
          },
          error: (_) => {
            this.sharedService.openSnackBar('There was an error generating the report', 'Dismiss');
          }
        });
    });
  }

}
